import { jsx, jsxs } from 'react/jsx-runtime';
import { CheckmarkCircleIcon, ExclamationmarkTriangleIcon, ExclamationmarkOctagonIcon } from '@flex-design-system/legacy-flex-icons';
import { AnimatePresence } from 'framer-motion';
import { useCallback, useMemo } from 'react';
import { createGlobalReferenceContext } from '../../utils/createGlobalReferenceContext.mjs';
import Portal from '../portal/Portal.mjs';
import { ToastContent } from './ToastContent.mjs';
import { useToastState } from './useToastState.mjs';

const ToastsContext = createGlobalReferenceContext(
  "fx-toast-context",
  {
    show: () => "",
    error: () => "",
    success: () => "",
    warning: () => "",
    update: () => {
    },
    hide: () => {
    }
  }
);
const CurrentToastContext = createGlobalReferenceContext(
  "fx-current-toast-context",
  []
);
const TOAST_MAX = 80;
const DEFAULT_ICON_SIZE = 26;
function ToastProvider({ children }) {
  const {
    toasts,
    show: showToast,
    update,
    hide
  } = useToastState({ limit: TOAST_MAX });
  const hasMultipleToast = toasts.length > 0;
  const allToastLength = toasts.length;
  const items = toasts.map((toast, index) => {
    return /* @__PURE__ */ jsx(
      ToastContent,
      {
        shouldHide: hasMultipleToast && index !== allToastLength - 1,
        toast,
        hide
      },
      toast.id
    );
  });
  const show = useCallback(
    (content, options = {}) => {
      return showToast({
        content,
        ...options
      });
    },
    [showToast]
  );
  const error = useCallback(
    (errorContent, errorToastOptions = {}) => {
      const {
        leftSlot = /* @__PURE__ */ jsx(
          ExclamationmarkOctagonIcon,
          {
            size: DEFAULT_ICON_SIZE,
            type: "fill",
            color: "orange"
          }
        ),
        ...rest
      } = errorToastOptions;
      return showToast({
        leftSlot,
        role: "alert",
        content: errorContent,
        ...rest
      });
    },
    [showToast]
  );
  const warning = useCallback(
    (warningContent, errorToastProps = {}) => {
      const {
        leftSlot = /* @__PURE__ */ jsx(
          ExclamationmarkTriangleIcon,
          {
            size: DEFAULT_ICON_SIZE,
            type: "fill",
            color: "yellow"
          }
        ),
        ...rest
      } = errorToastProps;
      return showToast({
        leftSlot,
        role: "alert",
        content: warningContent,
        ...rest
      });
    },
    [showToast]
  );
  const success = useCallback(
    (successContent, successToastProps = {}) => {
      const {
        leftSlot = /* @__PURE__ */ jsx(
          CheckmarkCircleIcon,
          {
            size: DEFAULT_ICON_SIZE,
            type: "fill",
            color: "green"
          }
        ),
        ...rest
      } = successToastProps;
      return showToast({
        leftSlot,
        role: "status",
        content: successContent,
        ...rest
      });
    },
    [showToast]
  );
  return /* @__PURE__ */ jsx(
    ToastsContext.Provider,
    {
      value: useMemo(
        () => ({
          show,
          error,
          warning,
          success,
          hide,
          update
        }),
        [error, hide, show, success, update, warning]
      ),
      children: /* @__PURE__ */ jsxs(CurrentToastContext.Provider, { value: toasts, children: [
        /* @__PURE__ */ jsx(Portal, { children: /* @__PURE__ */ jsx(AnimatePresence, { children: items }) }),
        children
      ] })
    }
  );
}

export { CurrentToastContext, ToastProvider, ToastsContext };
//# sourceMappingURL=ToastContext.mjs.map
